import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Layer } from "../../types/layer";
import LayerControlItem from "./LayerControlItem";
import FilterModal, { Filter } from "./FilterModal";
import { getAttributesFromLayer } from "../../utils/layerFilters";
import {
  useCustomLayers,
  useCreateCustomLayer,
  useDeleteCustomLayer,
  useUpdateCustomLayer,
} from "../../api/customLayer";
import UploadCustomLayerModal from "./UploadCustomLayerModal";
import LayerStyleEditor from "./LayerStyleEditor";
import { CustomLayer } from "../../types/customLayer";

interface CustomLayersControlProps {
  layer: Layer | null;
  filters: Filter[];
  onFiltersChange: (filters: Filter[]) => void;
  selectedIds: number[];
  onToggleLayer: (id: number) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

interface InlineStyleEditorProps {
  layer: CustomLayer;
  onColorChange: (color: string) => void;
  onOpacityCommit: (opacity: number) => void;
}

/**
 * Holds the slider's transient drag-state locally so the slider feels smooth
 * while the parent's CustomLayer object only updates on commit (via the
 * optimistic cache patch in useUpdateCustomLayer).
 */
const InlineStyleEditor: React.FC<InlineStyleEditorProps> = ({ layer, onColorChange, onOpacityCommit }) => {
  const [opacity, setOpacity] = useState(layer.opacity);
  useEffect(() => {
    setOpacity(layer.opacity);
  }, [layer.opacity]);

  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      <LayerStyleEditor
        size="small"
        showLabels={false}
        variant="dark"
        color={layer.color}
        opacity={opacity}
        onColorChange={onColorChange}
        onOpacityChange={setOpacity}
        onOpacityCommit={onOpacityCommit}
      />
    </Box>
  );
};

const CustomLayersControl: React.FC<CustomLayersControlProps> = ({ layer, filters, onFiltersChange, selectedIds, onToggleLayer, expanded, onExpandedChange }) => {
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const { projectId } = useParams<{ projectId: string }>();
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [styleEditingId, setStyleEditingId] = useState<number | null>(null);

  const { customLayers } = useCustomLayers(projectId);
  const createCustomLayer = useCreateCustomLayer(projectId || "");
  const deleteCustomLayer = useDeleteCustomLayer(projectId || "");
  const updateCustomLayer = useUpdateCustomLayer(projectId || "");

  const attributes = useMemo(() => {
    return layer?.rawSource ? getAttributesFromLayer(layer.rawSource) : [];
  }, [layer?.rawSource]);

  if (!layer) return null;

  const handleToggleVisibility = () => {
    layer.toolbarButton?.props?.onClick?.();
  };

  const handleFiltersChange = (newFilters: Filter[]) => {
    onFiltersChange(newFilters);
  };

  const handleUpload = async (name: string, filestoreFileId: number, color: string, opacity: number) => {
    await createCustomLayer.mutateAsync({ name, filestoreFileId, color, opacity });
  };

  const handleDeleteConfirm = (id: number) => {
    deleteCustomLayer.mutate(id);
    setDeleteTargetId(null);
  };

  const buttonSx = {
    color: "#00d4ad",
    borderColor: "#00d4ad",
    backgroundColor: "transparent",
    fontSize: "0.75rem",
    "&:hover": { backgroundColor: "rgba(0, 212, 173, 0.1)" },
  };

  const manageContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {!viewOnly && (
        <Button size="small" variant="outlined" onClick={() => setUploadModalOpen(true)} sx={buttonSx}>
          Upload Layer
        </Button>
      )}

      {(customLayers?.length ?? 0) > 0 && (
        <Box sx={{ mt: 0.5, borderTop: "1px solid rgba(148, 163, 184, 0.2)", pt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>
            Existing Layers
          </Typography>
          {customLayers?.map((cl) => {
            const isEditing = styleEditingId === cl.id;
            return (
              <Box key={cl.id} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title={selectedIds.includes(cl.id) ? "Hide layer" : "Show layer"}>
                    <IconButton size="small" onClick={() => onToggleLayer(cl.id)} sx={{ color: selectedIds.includes(cl.id) ? "#00d4ad" : "#64748b", padding: "2px" }}>
                      {selectedIds.includes(cl.id)
                        ? <VisibilityIcon sx={{ fontSize: "0.875rem" }} />
                        : <VisibilityOffIcon sx={{ fontSize: "0.875rem" }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isEditing ? "Close colour editor" : "Edit colour and opacity"}>
                    <Box
                      component="button"
                      onClick={() => { if (!viewOnly) setStyleEditingId(isEditing ? null : cl.id); }}
                      style={{ backgroundColor: cl.color }}
                      sx={{
                        width: 14,
                        height: 14,
                        padding: 0,
                        borderRadius: "3px",
                        border: isEditing
                          ? "2px solid #00d4ad"
                          : "1px solid rgba(148, 163, 184, 0.4)",
                        opacity: cl.opacity ?? 1,
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                      aria-label={`Edit colour for ${cl.name}`}
                    />
                  </Tooltip>
                  <Typography variant="caption" sx={{ color: "#e2e8f0", fontSize: "0.75rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {cl.name}
                  </Typography>
                  {viewOnly ? null : deleteTargetId === cl.id ? (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Button size="small" onClick={() => handleDeleteConfirm(cl.id)} sx={{ color: "#ef4444", minWidth: 0, fontSize: "0.65rem", p: "2px 6px" }}>
                        Delete
                      </Button>
                      <Button size="small" onClick={() => setDeleteTargetId(null)} sx={{ color: "#94a3b8", minWidth: 0, fontSize: "0.65rem", p: "2px 6px" }}>
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <IconButton size="small" onClick={() => setDeleteTargetId(cl.id)} sx={{ color: "#64748b", padding: "2px", "&:hover": { color: "#ef4444" } }}>
                      <DeleteIcon sx={{ fontSize: "0.875rem" }} />
                    </IconButton>
                  )}
                </Box>
                {isEditing && (
                  <InlineStyleEditor
                    layer={cl}
                    onColorChange={(color) =>
                      updateCustomLayer.mutate({ layerId: cl.id, color })
                    }
                    onOpacityCommit={(opacity) =>
                      updateCustomLayer.mutate({ layerId: cl.id, opacity })
                    }
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <UploadCustomLayerModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUpload}
        isPending={createCustomLayer.isPending}
      />
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Custom Data"
        attributes={attributes}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />
      <LayerControlItem
        title="Custom Data"
        icon="layers"
        visible={layer.active ?? false}
        onToggleVisibility={handleToggleVisibility}
        enabled={!!layer.rawSource}
        filterCount={filters.length}
        onFilterClick={() => setFilterModalOpen(true)}
        filterDisabled={attributes.length === 0}
        drawingContent={manageContent}
        drawingContentLabel="Manage Layers"
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        keepOpen={styleEditingId !== null || deleteTargetId !== null || uploadModalOpen}
        onOutsideClick={() => {
          setStyleEditingId(null);
          setDeleteTargetId(null);
        }}
      />
    </>
  );
};

export default CustomLayersControl;
